/**
 * engine.js — renderer, scene, camera rig, and the frame loop.
 *
 * The loop runs through `renderer.setAnimationLoop`, which transparently
 * becomes the XR frame callback once a session starts. That is the only way to
 * get one loop that is correct both on the desktop and inside a headset.
 *
 * Timing model: frame delta is clamped, accumulated, and drained in fixed
 * PHYSICS_DT slices. Whatever time is left over becomes the interpolation
 * alpha handed to `physics.syncMeshes(alpha)`, so meshes render smoothly even
 * though the simulation ticks at a fixed 72 Hz.
 *
 * @module core/engine
 */

import * as THREE from 'three';
import {
  PHYSICS_DT, MAX_FRAME_DT, MAX_SUBSTEPS, MAX_PIXEL_RATIO, EVENTS,
} from '../constants.js';
import { bus } from './events.js';
import * as physics from './physics.js';
import { options } from './testmode.js';

class Engine {
  constructor() {
    /** @type {THREE.WebGLRenderer} */ this.renderer = null;
    /** @type {THREE.Scene} */ this.scene = null;
    /** @type {THREE.PerspectiveCamera} */ this.camera = null;
    /**
     * The rig is the player's position in world space. In XR the headset pose
     * is applied to `camera` *relative to* the rig, so moving the player means
     * moving the rig — never the camera.
     * @type {THREE.Group}
     */
    this.cameraRig = null;

    this.frameCount = 0;
    this.fps = 0;
    this.elapsed = 0;

    this._accumulator = 0;
    this._lastTime = 0;
    this._fpsSamples = [];
    /** @type {Set<(dt:number, elapsed:number)=>void>} */
    this._updaters = new Set();
    /** @type {Array<{target:number, resolve:Function}>} */
    this._frameWaiters = [];
    this._running = false;
  }

  /**
   * Build renderer, scene and camera and attach the canvas.
   * @param {{ canvasParent?: HTMLElement }} [opts]
   */
  init({ canvasParent = document.body } = {}) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
      // Kept so screenshot tooling can read pixels back after a frame.
      preserveDrawingBuffer: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.xr.enabled = true;
    // 72 Hz is the Quest 3S target (GDD 12.2).
    if (this.renderer.xr.setFramebufferScaleFactor) {
      this.renderer.xr.setFramebufferScaleFactor(1.0);
    }

    canvasParent.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      65, window.innerWidth / window.innerHeight, 0.05, 400,
    );
    this.cameraRig = new THREE.Group();
    this.cameraRig.name = 'cameraRig';
    this.cameraRig.add(this.camera);
    this.scene.add(this.cameraRig);

    window.addEventListener('resize', () => this.onResize());
    this.onResize();

    bus.emit(EVENTS.ENGINE_READY, { engine: this });
    return this;
  }

  onResize() {
    if (!this.renderer) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO));
    bus.emit(EVENTS.RESIZE, { width: w, height: h });
  }

  /**
   * Register a per-frame callback.
   * @param {(dt: number, elapsed: number) => void} fn
   * @returns {() => void} Unregister.
   */
  onUpdate(fn) {
    this._updaters.add(fn);
    return () => this._updaters.delete(fn);
  }

  /** Start the frame loop. */
  start() {
    if (this._running) return;
    this._running = true;
    this._lastTime = performance.now();
    this.renderer.setAnimationLoop((time, xrFrame) => this._frame(time, xrFrame));
  }

  /** Stop the frame loop. */
  stop() {
    this._running = false;
    this.renderer.setAnimationLoop(null);
  }

  /**
   * Resolve after `n` more rendered frames. Physics advances with rendering,
   * so tests use this to let thrown objects fly and settle.
   * @param {number} n
   * @returns {Promise<number>} Frame count at resolution.
   */
  waitFrames(n = 1) {
    return new Promise((resolve) => {
      this._frameWaiters.push({ target: this.frameCount + Math.max(1, n), resolve });
    });
  }

  /**
   * One frame: fixed-step physics, then updaters, then render.
   * @param {number} time High-resolution timestamp from rAF/XR.
   * @param {XRFrame} [xrFrame]
   * @private
   */
  _frame(time, xrFrame) {
    const now = time || performance.now();
    let dt = (now - this._lastTime) / 1000;
    this._lastTime = now;

    // A clamp here is what stops a backgrounded tab from resuming with a
    // multi-second delta and detonating the simulation.
    if (!Number.isFinite(dt) || dt < 0) dt = 0;
    dt = Math.min(dt, MAX_FRAME_DT);
    this.elapsed += dt;

    this._trackFps(dt);

    // ── Fixed-step physics ────────────────────────────────────────────────
    let alpha = 1;
    if (physics.isReady() && !options.noPhysics) {
      this._accumulator += dt;
      let steps = 0;
      while (this._accumulator >= PHYSICS_DT && steps < MAX_SUBSTEPS) {
        physics.step();
        this._accumulator -= PHYSICS_DT;
        steps++;
      }
      // Shedding the backlog beats trying to catch up and stuttering forever.
      if (steps >= MAX_SUBSTEPS) this._accumulator = 0;
      alpha = THREE.MathUtils.clamp(this._accumulator / PHYSICS_DT, 0, 1);
      physics.syncMeshes(alpha);
    }

    // ── Game update ───────────────────────────────────────────────────────
    for (const fn of this._updaters) {
      try {
        fn(dt, this.elapsed, xrFrame);
      } catch (err) {
        console.error('[engine] updater threw:', err);
        if (Array.isArray(window.__ccErrors)) window.__ccErrors.push(`updater: ${err.message}`);
      }
    }

    bus.emit(EVENTS.FRAME, { dt, elapsed: this.elapsed, frame: this.frameCount, xrFrame });

    // ── Render ────────────────────────────────────────────────────────────
    this.renderer.render(this.scene, this.camera);

    this.frameCount++;
    this._resolveWaiters();
  }

  /** @private */
  _trackFps(dt) {
    if (dt <= 0) return;
    this._fpsSamples.push(1 / dt);
    if (this._fpsSamples.length > 45) this._fpsSamples.shift();
    let sum = 0;
    for (const s of this._fpsSamples) sum += s;
    this.fps = sum / this._fpsSamples.length;
  }

  /** @private */
  _resolveWaiters() {
    if (this._frameWaiters.length === 0) return;
    const still = [];
    for (const w of this._frameWaiters) {
      if (this.frameCount >= w.target) w.resolve(this.frameCount);
      else still.push(w);
    }
    this._frameWaiters = still;
  }

  /** @returns {{ calls:number, triangles:number, geometries:number, textures:number }} */
  renderStats() {
    const info = this.renderer.info;
    return {
      calls: info.render.calls,
      triangles: info.render.triangles,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
    };
  }
}

/** The one engine. */
export const engine = new Engine();
